import apiService from './api';
import type { ApiResponse } from '../types/api';
import type {
  ContractDiff, ContractEvent, ContractEventKind, ContractFamily, ContractFamilyDetail,
  ContractPublishResult, ContractTier,
} from '../types/contracts';

/**
 * Contracts: families of fixed, numbered versions (/api/cultivar/contracts).
 *
 * Drafting needs a cultivar pbr or admin seat; publishing and withdrawing need
 * the cultivar admin seat. Nothing here sends an agreement — issuing lives on
 * cultivarService, and always sends a family's published version.
 */
class ContractService {
  list(filters?: { license_tier?: ContractTier; for_cultivar_id?: number; published_only?: boolean }): Promise<ApiResponse<ContractFamily[]>> {
    const q = new URLSearchParams();
    if (filters?.license_tier) q.append('license_tier', filters.license_tier);
    if (filters?.for_cultivar_id) q.append('for_cultivar_id', String(filters.for_cultivar_id));
    if (filters?.published_only) q.append('published_only', 'true');
    const qs = q.toString();
    return apiService.get(`/cultivar/contracts${qs ? `?${qs}` : ''}`);
  }

  get(id: number): Promise<ApiResponse<ContractFamilyDetail>> {
    return apiService.get(`/cultivar/contracts/${id}`);
  }

  create(data: {
    name: string;
    license_tier: ContractTier;
    cultivar_id?: number | null;
    description?: string | null;
    body_richtext: string;
  }): Promise<ApiResponse<ContractFamilyDetail>> {
    return apiService.post('/cultivar/contracts', data);
  }

  updateDetails(id: number, data: { name?: string; description?: string | null }): Promise<ApiResponse<ContractFamilyDetail>> {
    return apiService.put(`/cultivar/contracts/${id}`, data);
  }

  /** Save the family's draft, starting one from this text if there is none. */
  saveDraft(id: number, body_richtext: string): Promise<ApiResponse<ContractFamilyDetail>> {
    return apiService.put(`/cultivar/contracts/${id}/draft`, { body_richtext });
  }

  discardDraft(id: number): Promise<ApiResponse<ContractFamilyDetail>> {
    return apiService.delete(`/cultivar/contracts/${id}/draft`);
  }

  /** Cultivar admin seat. `expected_content_hash` is the draft the admin read. */
  publish(id: number, change_summary: string, expected_content_hash?: string | null): Promise<ApiResponse<ContractPublishResult>> {
    return apiService.post(`/cultivar/contracts/${id}/publish`, { change_summary, expected_content_hash: expected_content_hash ?? null });
  }

  /** Cultivar admin seat. Stops the contract being sent at all. */
  withdraw(id: number, reason: string): Promise<ApiResponse<ContractFamilyDetail>> {
    return apiService.post(`/cultivar/contracts/${id}/withdraw`, { reason });
  }

  diff(id: number, fromVersionId: number, toVersionId: number): Promise<ApiResponse<ContractDiff>> {
    return apiService.get(`/cultivar/contracts/${id}/diff?from=${fromVersionId}&to=${toVersionId}`);
  }

  events(kind: ContractEventKind, id: number): Promise<ApiResponse<ContractEvent[]>> {
    return apiService.get(`/cultivar/contract-events/${kind}/${id}`);
  }

  /** Everything that happened to this organisation's contracts. */
  orgEvents(limit = 200): Promise<ApiResponse<ContractEvent[]>> {
    return apiService.get(`/cultivar/contracts/events?limit=${limit}`);
  }
}

const contractService = new ContractService();
export default contractService;
